/**
 * TagEditor — edit a todo's tag set: current tags as removable chips plus an
 * input with autocomplete sourced from the household's known tags.
 *
 * - Enter (or picking a suggestion) adds the current input as a tag.
 * - × on a chip removes it.
 * - Adds are trimmed and deduped case-insensitively against the current set.
 * - Emits the full desired string[] via onChange on every mutation (the
 *   server replace-sets tags, so the parent persists the whole array).
 *
 * Suggestions are the known household tags (fetched once via getTags), minus
 * tags already applied, filtered by the typed prefix/substring. Filtering is
 * debounced like the mention autocomplete so fast typing doesn't thrash the
 * list. The catalog fetch is best-effort: a failure leaves the user free to
 * type arbitrary tags (the input still works without suggestions).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTags } from '@/infra/api/client';
import { TagChip } from './TagChip';
import { addTag, removeTag, tagKey, normalizeTagInput } from '../tags';

export interface TagEditorProps {
  tags: string[];
  onChange: (next: string[]) => void;
}

const DEBOUNCE_MS = 150;

export function TagEditor({ tags, onChange }: TagEditorProps) {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [known, setKnown] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Load the household tag catalog once on mount. Best-effort: on failure the
  // editor still accepts free-form tags, just without suggestions.
  useEffect(() => {
    let cancelled = false;
    getTags()
      .then((all) => {
        if (!cancelled) setKnown(all);
      })
      .catch(() => {
        // No suggestions; manual entry still works.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce the query that drives suggestion filtering.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(input), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [input]);

  const appliedKeys = useMemo(() => new Set(tags.map(tagKey)), [tags]);

  const suggestions = useMemo(() => {
    const q = tagKey(debounced);
    return known
      .filter((t) => !appliedKeys.has(tagKey(t)))
      .filter((t) => (q ? tagKey(t).includes(q) : true));
  }, [known, appliedKeys, debounced]);

  // Keep the active suggestion index in range as the list shrinks/grows.
  useEffect(() => {
    setActiveIndex((i) => (i >= suggestions.length ? 0 : i));
  }, [suggestions.length]);

  const commit = useCallback(
    (raw: string) => {
      const trimmed = normalizeTagInput(raw);
      if (!trimmed) return;
      const next = addTag(tags, trimmed);
      if (next !== tags) onChange(next);
      setInput('');
      setActiveIndex(0);
    },
    [tags, onChange],
  );

  const handleRemove = useCallback(
    (label: string) => {
      const next = removeTag(tags, label);
      if (next !== tags) onChange(next);
    },
    [tags, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const showing = suggestions.length > 0 && input.trim().length > 0;
      if (e.key === 'Enter') {
        e.preventDefault();
        if (showing && suggestions[activeIndex]) {
          commit(suggestions[activeIndex]);
        } else {
          commit(input);
        }
        return;
      }
      if (e.key === 'ArrowDown' && showing) {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp' && showing) {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      // Backspace on an empty input removes the last applied tag.
      if (e.key === 'Backspace' && input.length === 0 && tags.length > 0) {
        e.preventDefault();
        handleRemove(tags[tags.length - 1]);
      }
    },
    [suggestions, input, activeIndex, commit, tags, handleRemove],
  );

  const showSuggestions = suggestions.length > 0 && input.trim().length > 0;
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="tag-editor">
      <div className="tag-editor__chips">
        {tags.map((t) => (
          <TagChip key={tagKey(t)} label={t} onRemove={handleRemove} />
        ))}
      </div>
      <div className="tag-editor__input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="tag-editor__input"
          value={input}
          placeholder="Add a tag…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Add a tag"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-autocomplete="list"
        />
        {showSuggestions && (
          <div className="tag-dropdown" role="listbox">
            {suggestions.map((t, i) => {
              const isActive = i === activeIndex;
              return (
                <button
                  key={tagKey(t)}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`tag-dropdown__item${isActive ? ' tag-dropdown__item--active' : ''}`}
                  onMouseDown={(e) => {
                    // Keep input focus so a follow-up keystroke still lands.
                    e.preventDefault();
                    commit(t);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  #{t}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
